import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    
    // Store discount settings from the block
    this.discountPercentage = parseFloat(this.dataset.discountPercentage) || 0;
    this.badgeText = this.dataset.badgeText || '';
    this.showOriginalCompareAtPrice = this.dataset.showOriginalCompareAtPrice === 'true';
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Check if HTML was provided (standard variant picker)
    if (event.detail.data.html) {
      const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
      const currentPrice = this.querySelector('[ref="priceContainer"]');

      if (!newPrice || !currentPrice) return;

      if (currentPrice.innerHTML !== newPrice.innerHTML) {
        currentPrice.replaceWith(newPrice);
      }
    } else if (event.detail.resource) {
      // Vue variant picker - update price directly from variant data
      this.updatePriceFromVariant(event.detail.resource);
    }
  };

  /**
   * Updates price display from variant data (for Vue variant picker)
   * @param {Object} variant - The variant object with price information
   */
  updatePriceFromVariant(variant) {
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (!currentPrice) return;

    // Format price using Shopify money format
    const formatMoney = (cents) => {
      const format = window.Shopify?.currency?.format || '${{amount}}';
      const value = (cents / 100).toFixed(2);
      return format.replace('{{amount}}', value);
    };

    // Apply discount if discount_percentage is greater than 0
    let displayPrice = variant.price;
    let displayCompareAtPrice = variant.compare_at_price;
    
    if (this.discountPercentage > 0) {
      // If show_original_compare_at_price is true and original compare_at_price exists, use it
      if (this.showOriginalCompareAtPrice && variant.compare_at_price > 0) {
        displayCompareAtPrice = variant.compare_at_price;
      } else {
        displayCompareAtPrice = variant.price;
      }
      const discountMultiplier = (100 - this.discountPercentage) / 100;
      displayPrice = Math.round(variant.price * discountMultiplier);
    }

    // Build new price HTML
    let priceHTML = '';
    
    // Check if there's a sale (compare_at_price > price)
    const hasCompareAtPrice = (displayCompareAtPrice && displayCompareAtPrice > displayPrice) || this.discountPercentage > 0;
    
    if (hasCompareAtPrice) {
      // Show sale price first, then compare at price
      priceHTML = `
        <span role="group">
          <span class="visually-hidden">Sale price: </span>
          <span class="price">${formatMoney(displayPrice)}</span>
        </span>
        <span role="group">
          <span class="visually-hidden">Original price, crossed out: </span>
          <span class="compare-at-price" aria-label="Original price, crossed out: ${formatMoney(displayCompareAtPrice)}">
            ${formatMoney(displayCompareAtPrice)}
          </span>
        </span>
      `;
    } else {
      // Show regular price only
      priceHTML = `
        <span class="price">${formatMoney(displayPrice)}</span>
      `;
    }

    // Add badge if badge text is provided
    if (this.badgeText && this.badgeText.trim() !== '') {
      priceHTML += `
        <span class="!block whitespace-nowrap text-left font-semibold text-[12px] md:text-[14px]">
          <span class="border-2 border-[#56A511] block font-semibold md:text-[16px] px-2 py-[2px] text-[14px] text-[#56A511] text-left">
            ${this.badgeText}
          </span>
        </span>
      `;
    }

    currentPrice.innerHTML = priceHTML;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
